import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  IconButton,
  Snackbar,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import dayjs from "dayjs";
import { MONO, adminChipSx } from "./adminStyles";

/**
 * Full detail for one row of the Events table.
 *
 * The table truncates `props` to a single ellipsised line, which is fine for
 * counting events and useless for debugging one. That matters most for
 * `server_error`, whose props carry the route, method, error name and message —
 * i.e. everything you need to triage a production 500, all of it past the ellipsis.
 *
 * Deliberately generic rather than server_error-specific: every event type gets the
 * same treatment, so this does not have to grow a branch per type.
 *
 * Purely presentational — the row already holds the whole event, so there is
 * nothing to fetch.
 */
const EventDetailDialog = ({ open, onClose, event, accent }) => {
  const theme = useTheme();
  const [snack, setSnack] = useState("");

  const copyForLlm = useCallback(() => {
    if (!event) return;
    // One self-contained blob: paste straight into an agent to write the issue.
    const payload = {
      id: event.id ?? null,
      event_type: event.event_type ?? null,
      user_email: event.user_email ?? null,
      source: event.source ?? null,
      app_version: event.app_version ?? null,
      occurred_at: event.occurred_at ?? null,
      created_at: event.created_at ?? null,
      created_at_iso: event.created_at
        ? dayjs(event.created_at).toISOString()
        : null,
      props: event.props ?? null,
    };
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .then(() => setSnack("Event JSON copied"))
      .catch(() => setSnack("Copy failed"));
  }, [event]);

  if (!event) return null;

  const metaRow = (label, value) => (
    <Box sx={{ display: "flex", gap: 2, alignItems: "baseline" }}>
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 700,
          minWidth: 104,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: "0.82rem",
          color: theme.palette.text.primary,
          wordBreak: "break-all",
        }}
      >
        {value === null || value === undefined || value === "" ? "—" : value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Chip
                label={event.event_type}
                size="small"
                sx={adminChipSx(theme, accent)}
              />
              <Typography sx={{ color: theme.palette.text.secondary }}>
                {event.created_at
                  ? dayjs(event.created_at).format("MMM D, YYYY HH:mm:ss")
                  : "—"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={copyForLlm}
                data-testid="event-copy-for-llm"
              >
                Copy for LLM
              </Button>
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Stack spacing={0.75} sx={{ mb: 2.5 }}>
            {metaRow("Event id", event.id)}
            {metaRow("User", event.user_email)}
            {metaRow("Source", event.source)}
            {metaRow("App version", event.app_version)}
            {metaRow(
              "Occurred",
              event.occurred_at
                ? dayjs(event.occurred_at).format("MMM D, YYYY HH:mm:ss")
                : null
            )}
          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 700,
            }}
          >
            Props
          </Typography>
          <Box
            data-testid="event-props-json"
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
              fontFamily: MONO,
              fontSize: "0.78rem",
              color: theme.palette.text.primary,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 420,
              overflowY: "auto",
            }}
          >
            {event.props ? JSON.stringify(event.props, null, 2) : "—"}
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={2000}
        onClose={() => setSnack("")}
        message={snack}
      />
    </>
  );
};

export default EventDetailDialog;
